import { useCallback, useMemo } from 'react';

import { useTypedDispatch, useTypedSelector } from 'store';
import { RootStore } from 'store';
import { Bus } from 'tools';
import {
  arbitrageFiltersSheme,
  cexPairFiltersScheme,
  dexPairFiltersScheme,
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
  generalWalletsFiltersScheme,
  projectFiltersScheme,
  solPairFiltersScheme,
} from 'types/filters';
import { IFilterValue } from 'types/filters/common';
import { ICexPair, IDexPair } from 'types/pairs';
import { EExchange } from 'web3';

import { useGetFilters } from './useGetFilters';
import { useIsFiltersSet } from './useIsFiltersSet';

import {
  addFilter as addStoreFilter,
  clearFilters as clearStoreFilter,
  deleteFilter as deleteStoreFilter,
  saveFilters as saveStoreFilters,
  updateFilter as updateStoreFilter,
} from '..';
import { getProjectFiltersKey } from '../utils';

const EMPTY_FILTERS: IFilterValue[] = [];

interface IProps {
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  cex?: {
    pair?: ICexPair;
    type: ECexPairFilters;
  };
  dex?: {
    pair?: IDexPair;
    type: EDexPairFilters;
  };
  sol?: {
    pair?: IDexPair;
    type: ESolPairFilters;
  };
  project?: {
    projectId?: number;
    type: EProjectFilters;
  };
}

export function useFilters({ cex, dex, general, arbitrage, sol, project }: IProps = {}) {
  const dispatch = useTypedDispatch();
  const emptyFilters = useMemo(() => [] as IFilterValue[], []);
  const noop = useCallback(() => {}, []);
  const isAdmin = useTypedSelector(state => state.auth?.isAdmin ?? false);

  const hasScope = Boolean(
    general || arbitrage || cex || dex || sol || (project && project.projectId),
  );

  const normalizedProject = useMemo(() => {
    if (!project?.projectId) return undefined;
    return { projectId: project.projectId, type: project.type };
  }, [project?.projectId, project?.type]);

  const getPairFilters = useGetFilters({
    cex,
    dex,
    general,
    arbitrage,
    sol,
    project: normalizedProject,
  });
  const rawIsFilterSetted = useIsFiltersSet({
    cex,
    dex,
    general,
    arbitrage,
    sol,
    project: normalizedProject,
  });

  const pairFiltersScheme = useMemo(() => {
    if (!hasScope) return undefined;

    return general && general === EGeneralFilters.wallets
      ? generalWalletsFiltersScheme
      : arbitrage
        ? arbitrageFiltersSheme({ arbitrage })
        : project?.projectId
          ? projectFiltersScheme({ type: project.type, isAdmin })
          : dex
            ? dexPairFiltersScheme({ pair: dex.pair!, type: dex.type, isAdmin })
            : cex
              ? cexPairFiltersScheme({ pair: cex.pair!, type: cex.type, isAdmin })
              : sol
                ? solPairFiltersScheme({ pair: sol.pair!, type: sol.type })
                : undefined;
  }, [general, arbitrage, project, dex, cex, sol, hasScope, isAdmin]);

  const pairFilters = useMemo(
    () => (hasScope ? getPairFilters() : emptyFilters),
    [getPairFilters, hasScope, emptyFilters],
  );

  const settedPairFiltersSelector = useCallback(
    (store: RootStore) => {
      if (general) return store.filters[general]?.setted ?? EMPTY_FILTERS;

      if (dex)
        return store.filters.dex[dex.pair?.id ?? 0]?.filters[dex.type]?.setted ?? EMPTY_FILTERS;

      if (cex)
        return store.filters.cex[cex.pair?.id ?? 0]?.filters[cex.type]?.setted ?? EMPTY_FILTERS;

      if (sol)
        return store.filters.sol[sol.pair?.id ?? 0]?.filters[sol.type]?.setted ?? EMPTY_FILTERS;

      if (arbitrage) return store.filters.arbitrage[arbitrage]?.setted ?? EMPTY_FILTERS;

      if (project?.projectId) {
        const key = getProjectFiltersKey(project.projectId, project.type);
        return store.filters.project[key]?.setted ?? EMPTY_FILTERS;
      }

      return EMPTY_FILTERS;
    },
    [general, arbitrage, dex, cex, sol, project],
  );

  const rawSettedPairFilters = useTypedSelector(settedPairFiltersSelector);
  const settedPairFilters = hasScope ? rawSettedPairFilters : emptyFilters;
  const isFilterSetted = hasScope ? rawIsFilterSetted : false;

  const addFilter = useCallback(
    (filter?: IFilterValue) => {
      if (!hasScope) return;
      dispatch(
        addStoreFilter({ filter, general, arbitrage, dex, cex, sol, project: normalizedProject }),
      );
    },
    [dispatch, general, arbitrage, dex, cex, sol, normalizedProject, hasScope],
  );

  const updateFilter = useCallback(
    function (id: string, filter?: IFilterValue | undefined) {
      if (!hasScope) return;
      dispatch(
        updateStoreFilter({
          id,
          filter,
          general,
          arbitrage,
          cex,
          dex,
          sol,
          project: normalizedProject,
        }),
      );
    },
    [dispatch, general, arbitrage, cex, dex, sol, normalizedProject, hasScope],
  );

  const deleteFilter = useCallback(
    (id: string) => {
      if (!hasScope) return;
      dispatch(
        deleteStoreFilter({ id, general, arbitrage, cex, dex, sol, project: normalizedProject }),
      );
    },
    [dispatch, general, arbitrage, cex, dex, sol, normalizedProject, hasScope],
  );

  const clearFilters = useCallback(() => {
    if (!hasScope) return;
    dispatch(clearStoreFilter({ general, arbitrage, cex, dex, sol, project: normalizedProject }));
  }, [dispatch, general, arbitrage, cex, dex, sol, normalizedProject, hasScope]);

  const saveFilters = useCallback(
    (opts?: { update: boolean }) => {
      const update = opts?.update ?? false;

      if (!hasScope) return;

      dispatch(saveStoreFilters({ general, arbitrage, cex, dex, sol, project: normalizedProject }));

      setTimeout(() => {
        if (update) {
          if (general) {
            Bus.emit(`${general}:FILTER`);
          }
          if (cex) {
            Bus.emit(`${EExchange.cex}:${cex.type}:FILTER`);
          }
          if (dex) {
            Bus.emit(`${EExchange.dex}:${dex.type}:FILTER`);
          }
          if (sol) {
            Bus.emit(`${EExchange.sol}:${sol.type}:FILTER`);
          }
          if (arbitrage) {
            Bus.emit(`arbitrage:${arbitrage}:FILTER`);
          }
          if (project?.projectId) {
            Bus.emit(`project:${project.projectId}:${project.type}:FILTER`);
          }
        }
      }, 0);
    },
    [dispatch, general, arbitrage, cex, dex, sol, normalizedProject, hasScope, project],
  );

  if (!hasScope) {
    return {
      pairFilters: emptyFilters,
      settedPairFilters: emptyFilters,
      addFilter: noop,
      updateFilter: noop,
      deleteFilter: noop,
      isFilterSetted: false,
      scheme: undefined,
      clearFilters: noop,
      saveFilters: noop,
    };
  }

  return {
    pairFilters,
    settedPairFilters: settedPairFilters as IFilterValue[],
    addFilter,
    updateFilter,
    deleteFilter,
    isFilterSetted,
    scheme: pairFiltersScheme,
    clearFilters,
    saveFilters,
  };
}
